import {useState, useCallback, useEffect, useRef} from "react";
import {ReactComponent as DropdownOpened} from "assets/image/Layout/ObjectPanel/Layer/ic_dropdown.svg";
import {ReactComponent as DropdownClosed} from "assets/image/Layout/ObjectPanel/Layer/ic_dropdown_right.svg";
import {ReactComponent as ServerIcon} from "assets/image/Layout/ObjectPanel/ScriptTab/serverIcon.svg";
import {ReactComponent as ClientIcon} from "assets/image/Layout/ObjectPanel/ScriptTab/clientIcon.svg";
import {ObjectIcon} from "../../../AssetLayer/DraggableTree/TreeNode/icons";
import styles from "./index.module.scss";
import CustomTooltip from "components/Common/Tooltip/CustomTooltip";
import SelectDropDown from "components/Common/Dropdown/SelectDropDown";

export default function ScriptNode({
  nodeObject,
  depth,
  selectedKeys,
  selectNodeObject,
  onClickScriptItem,
  addUserScript,
  removeUserScript,
}) {
  const {name, type, title, uuid, userData} = nodeObject;
  const {serverScripts, clientScripts} = userData.objectScripts;
  const isSelected = uuid === selectedKeys[0];

  const [isExpanded, setIsExpanded] = useState(true);

  const onClickDropdown = useCallback(() => {
    setIsExpanded(!isExpanded);
    nodeObject.isExpanded = !isExpanded;
  }, [isExpanded]);

  const onClickScriptNode = useCallback(() => {
    selectNodeObject(uuid, type);
  }, [uuid, type]);

  const node = useRef();
  const childrenGap = useRef();

  const titleWidth = 133 - 20 * depth;
  const childrenGapWidth = 208 - 20 * (depth + 1);

  const [menuOpened, setMenuOpened] = useState(false);
  const handleMenuOpen = e => {
    e.stopPropagation();
    onClickScriptNode();
    setMenuOpened(!menuOpened);
  };

  return (
    <div className={styles.container} uuid={uuid}>
      <div
        className={`${styles.content} ${isSelected ? styles.selected_node : ""}`}
        ref={node}
        location={"node"}
        onClick={onClickScriptNode}
      >
        <div className={styles.icon_front}>
          {serverScripts && <DropdownIcon isExpanded={isExpanded} onClick={onClickDropdown} />}
          <ObjectIcon type={type} name={name} userData={userData} />
        </div>
        <div className={styles.title} name={title} style={{width: titleWidth}}>
          {title}
        </div>
        <div className={styles.plus}>
          <div className={`${menuOpened ? styles.script_add_btn_on : styles.script_add_btn}`} onClick={handleMenuOpen}>
            {menuOpened && (
              <SelectDropDown style={{width: "152px", right: "60px"}} noRel={true} closeDropDown={() => setMenuOpened(false)}>
                <div
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpened(false);
                    addUserScript("clientScript");
                  }}
                >
                  Client Script
                </div>
                <div
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpened(false);
                    addUserScript("serverScript");
                  }}
                >
                  Server Script
                </div>
              </SelectDropDown>
            )}
          </div>
        </div>
      </div>
      {isExpanded && (
        <>
          <div className={styles.gap}>
            <div
              className={styles.gap_indicator}
              style={{width: childrenGapWidth, marginLeft: 240 - childrenGapWidth, zIndex: depth}}
              ref={childrenGap}
              location={"childrenGap"}
            >
              <div className={styles.gap_area}></div>
            </div>
          </div>
          <div className={styles.children} onClick={onClickScriptNode}>
            <>
              {serverScripts.length > 0 && (
                <ScriptGroup
                  objUuid={nodeObject.uuid}
                  type={"serverScript"}
                  scripts={serverScripts}
                  onClickScriptItem={onClickScriptItem}
                  addUserScript={addUserScript}
                  removeUserScript={removeUserScript}
                  onClickScriptNode={onClickScriptNode}
                />
              )}
              {clientScripts.length > 0 && (
                <ScriptGroup
                  objUuid={nodeObject.uuid}
                  type={"clientScript"}
                  scripts={clientScripts}
                  onClickScriptItem={onClickScriptItem}
                  addUserScript={addUserScript}
                  removeUserScript={removeUserScript}
                  onClickScriptNode={onClickScriptNode}
                />
              )}
            </>
          </div>
        </>
      )}
    </div>
  );
}

function DropdownIcon({isExpanded, onClick}) {
  if (isExpanded) return <DropdownOpened className={styles.dropdown} onClick={onClick} />;
  else return <DropdownClosed className={styles.dropdown} onClick={onClick} />;
}

const ScriptGroup = ({objUuid, type, scripts, onClickScriptItem, removeUserScript, onClickScriptNode}) => {
  return (
    <div className={styles.scripts_wrapper}>
      {scripts.map((script, index) => (
        <ScriptItem
          objUuid={objUuid}
          type={type}
          index={index}
          script={script}
          onClickScriptItem={onClickScriptItem}
          removeUserScript={removeUserScript}
          onClickScriptNode={onClickScriptNode}
        />
      ))}
    </div>
  );
};

const ScriptItem = ({objUuid, type, index, script, onClickScriptItem, removeUserScript, onClickScriptNode}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const handleMenuOpen = e => {
    e.stopPropagation();
    onClickScriptNode();
    setMenuOpened(!menuOpened);
  };

  return (
    <div className={styles.script_container} onClick={() => onClickScriptItem(objUuid, type, index, script.title, script.source)}>
      <div className={styles.script_content}>
        <CustomTooltip description={scriptType[type] && `${scriptType[type]} Script`} placement={"topLeft"}>
          <div className={styles.script_icon}>
            {scriptType[type] === "Server" && (
              <>
                <ServerIcon />
                <div className={styles.script_icon_text}>S</div>
              </>
            )}
            {scriptType[type] === "Client" && (
              <>
                <ClientIcon />
                <div className={styles.script_icon_text}>C</div>
              </>
            )}
          </div>
        </CustomTooltip>
        <div className={styles.script_title}>{script.title || "Untitled Script"}</div>
        <div className={`${menuOpened ? styles.script_more_btn_on : styles.script_more_btn}`} onClick={handleMenuOpen}>
          {menuOpened && (
            <SelectDropDown style={{width: "152px", right: "66px"}} noRel={true} closeDropDown={() => setMenuOpened(false)}>
              <div
                onClick={() => {
                  setMenuOpened(false);
                  removeUserScript(type, script.title, index);
                }}
              >
                Remove
              </div>
            </SelectDropDown>
          )}
          {/* <ul>
            <li
              onClick={() => {
                setMenuOpened(false);
                removeUserScript(type, index);
              }}
            >
              Remove
            </li>
          </ul> */}
        </div>
      </div>
    </div>
  );
};

const scriptType = {
  serverScript: "Server",
  clientScript: "Client",
};
